import React, { useEffect, useRef, useState, useCallback } from 'react';
import './App.css';

const W = 1200, H = 400;
const GROUND_Y = 320;
const GRAVITY = 0.6;
const JUMP_FORCE = -13;
const DINO_W = 44, DINO_H = 48;
const DUCK_H = 30;

export default function App() {
  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const [score, setScore] = useState(0);
  const [hi, setHi] = useState(() => parseInt(localStorage.getItem('dinoHi') || '0'));
  const [gameOver, setGameOver] = useState(false);
  const [started, setStarted] = useState(false);

  const initGame = useCallback(() => {
    return {
      dino: { x: 80, y: GROUND_Y - DINO_H, vy: 0, jumping: false, ducking: false, frame: 0 },
      obstacles: [],
      clouds: Array.from({ length: 4 }, (_, i) => ({ x: 300 * i + Math.random() * 200, y: 40 + Math.random() * 80 })),
      stars: Array.from({ length: 20 }, () => ({ x: Math.random() * W, y: Math.random() * 200, r: Math.random() * 2 + 0.5, twinkle: Math.random() * Math.PI * 2 })),
      ground1: 0, ground2: W,
      speed: 2, score: 0, frame: 0, spawnTimer: 0, night: 0, nightT: 0,
      particles: [], running: true
    };
  }, []);

  const start = useCallback(() => {
    gameRef.current = initGame();
    setScore(0);
    setGameOver(false);
    setStarted(true);
  }, [initGame]);

  useEffect(() => {
    const keys = {};
    const onDown = e => {
      if (['Space', 'ArrowUp', 'ArrowDown'].includes(e.code)) e.preventDefault();
      keys[e.code] = true;
      if (!started && (e.code === 'Space' || e.code === 'ArrowUp')) start();
      if (gameOver && e.code === 'Space') start();
    };
    const onUp = e => { keys[e.code] = false; };
    const onTouch = () => {
      if (!started) start();
      else if (gameOver) start();
      else if (gameRef.current) {
        const d = gameRef.current.dino;
        if (!d.jumping) { d.vy = JUMP_FORCE; d.jumping = true; }
      }
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('touchstart', onTouch, { passive: true });

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    let animId;
    const loop = () => {
      const g = gameRef.current;
      if (!g || !g.running) { animId = requestAnimationFrame(loop); draw(ctx, g, keys); return; }

      // Input
      const d = g.dino;
      if ((keys.Space || keys.ArrowUp) && !d.jumping) { d.vy = JUMP_FORCE; d.jumping = true; }
      d.ducking = !!keys.ArrowDown && !d.jumping;

      // Physics
      d.vy += GRAVITY;
      d.y += d.vy;
      const dinoH = d.ducking ? DUCK_H : DINO_H;
      const floorY = GROUND_Y - dinoH;
      if (d.y >= floorY) { d.y = floorY; d.vy = 0; d.jumping = false; }

      // Speed
      g.speed = 2 + g.score * 0.003;
      if (g.speed > 12) g.speed = 12;

      // Ground scroll
      g.ground1 -= g.speed;
      g.ground2 -= g.speed;
      if (g.ground1 <= -W) g.ground1 = g.ground2 + W;
      if (g.ground2 <= -W) g.ground2 = g.ground1 + W;

      // Clouds
      g.clouds.forEach(c => { c.x -= g.speed * 0.3; if (c.x < -100) { c.x = W + 50; c.y = 40 + Math.random() * 80; } });

      // Night cycle
      g.nightT += 0.0008;
      g.night = Math.max(0, Math.min(1, Math.sin(g.nightT) * 1.5));

      // Spawn obstacles
      g.spawnTimer -= g.speed;
      if (g.spawnTimer <= 0) {
        const type = Math.random();
        if (type < 0.35) {
          // Small cactus
          g.obstacles.push({ type: 'cactus-sm', x: W + 20, y: GROUND_Y - 36, w: 20, h: 36 });
        } else if (type < 0.6) {
          // Tall cactus
          g.obstacles.push({ type: 'cactus-lg', x: W + 20, y: GROUND_Y - 50, w: 26, h: 50 });
        } else if (type < 0.8) {
          // Double cactus
          g.obstacles.push({ type: 'cactus-sm', x: W + 20, y: GROUND_Y - 36, w: 20, h: 36 });
          g.obstacles.push({ type: 'cactus-sm', x: W + 50, y: GROUND_Y - 36, w: 20, h: 36 });
        } else {
          // Pterodactyl
          const py = Math.random() < 0.5 ? GROUND_Y - 70 : GROUND_Y - 40;
          g.obstacles.push({ type: 'bird', x: W + 20, y: py, w: 40, h: 30, frame: 0 });
        }
        g.spawnTimer = 200 + Math.random() * 300;
      }

      // Move obstacles
      g.obstacles.forEach(o => {
        o.x -= g.speed;
        if (o.type === 'bird') o.frame = (o.frame || 0) + 0.08;
      });
      g.obstacles = g.obstacles.filter(o => o.x > -60);

      // Collision
      const dx = d.x + 6, dy = d.y + 4, dw = DINO_W - 12, dh2 = dinoH - 8;
      for (const o of g.obstacles) {
        const ox = o.x + 4, oy = o.y + 4, ow = o.w - 8, oh = o.h - 8;
        if (dx < ox + ow && dx + dw > ox && dy < oy + oh && dy + dh2 > oy) {
          g.running = false;
          // Death particles
          for (let i = 0; i < 12; i++) {
            g.particles.push({ x: d.x + DINO_W / 2, y: d.y + dinoH / 2, vx: (Math.random() - 0.5) * 8, vy: (Math.random() - 0.5) * 8, life: 30 + Math.random() * 20 });
          }
          setGameOver(true);
          if (g.score > hi) { setHi(g.score); localStorage.setItem('dinoHi', g.score.toString()); }
          break;
        }
      }

      // Score
      g.frame++;
      if (g.frame % 6 === 0 && g.running) { g.score++; setScore(g.score); }

      // Dino run animation
      if (!d.jumping && !d.ducking) d.frame += 0.15;

      // Particles
      g.particles.forEach(p => { p.x += p.vx; p.y += p.vy; p.vy += 0.2; p.life--; });
      g.particles = g.particles.filter(p => p.life > 0);

      draw(ctx, g, keys);
      animId = requestAnimationFrame(loop);
    };

    animId = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(animId); window.removeEventListener('keydown', onDown); window.removeEventListener('keyup', onUp); window.removeEventListener('touchstart', onTouch); };
  }, [started, gameOver, hi, start]);

  function draw(ctx, g, keys) {
    if (!g) {
      // Title screen
      const night = false;
      drawBg(ctx, night, 0, []);
      drawGround(ctx, 0, W, night);
      drawDino(ctx, 80, GROUND_Y - DINO_H, false, false, 0);
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 36px "SF Pro Display", system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('🦖 DINO RUN', W / 2, H / 2 - 30);
      ctx.font = '18px "SF Pro Display", system-ui, sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.fillText('Press SPACE or tap to start', W / 2, H / 2 + 10);
      ctx.textAlign = 'left';
      return;
    }

    const { dino: d, obstacles, clouds, ground1, ground2, night, stars, particles } = g;

    drawBg(ctx, night, g.nightT, stars);
    clouds.forEach(c => drawCloud(ctx, c.x, c.y, night));
    drawGround(ctx, ground1, ground2, night);
    obstacles.forEach(o => {
      if (o.type === 'bird') drawBird(ctx, o.x, o.y, o.frame, night);
      else drawCactus(ctx, o.x, o.y, o.w, o.h, night);
    });
    drawDino(ctx, d.x, d.y, d.ducking, d.jumping, d.frame);

    // Particles
    particles.forEach(p => {
      ctx.globalAlpha = p.life / 50;
      ctx.fillStyle = '#ff6b6b';
      ctx.fillRect(p.x - 2, p.y - 2, 4, 4);
    });
    ctx.globalAlpha = 1;

    if (!g.running) {
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 32px "SF Pro Display", system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('GAME OVER', W / 2, H / 2 - 20);
      ctx.font = '16px "SF Pro Display", system-ui, sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.fillText('Press SPACE or tap to restart', W / 2, H / 2 + 15);
      ctx.textAlign = 'left';
    }
  }

  function lerpColor(a, b, t) {
    const p = (c) => parseInt(c, 16);
    const r = Math.round(p(a.slice(1,3)) * (1-t) + p(b.slice(1,3)) * t);
    const g = Math.round(p(a.slice(3,5)) * (1-t) + p(b.slice(3,5)) * t);
    const bl = Math.round(p(a.slice(5,7)) * (1-t) + p(b.slice(5,7)) * t);
    return `rgb(${r},${g},${bl})`;
  }

  function drawBg(ctx, n, t, stars) {
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, lerpColor('#87CEEB', '#0a0a2e', n));
    grad.addColorStop(0.5, lerpColor('#B0E0E6', '#1a1a4e', n));
    grad.addColorStop(1, lerpColor('#E0F0FF', '#2a1a3e', n));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    if (n > 0.05 && stars) {
      stars.forEach(s => {
        const alpha = n * (0.4 + 0.6 * Math.abs(Math.sin(s.twinkle + t * 3)));
        ctx.globalAlpha = alpha;
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      // Moon
      ctx.globalAlpha = n;
      ctx.fillStyle = '#ffffcc';
      ctx.beginPath();
      ctx.arc(W - 120, 60, 30, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = lerpColor('#87CEEB', '#0a0a2e', n);
      ctx.beginPath();
      ctx.arc(W - 110, 55, 26, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }

  function drawGround(ctx, g1, g2, n) {
    ctx.fillStyle = lerpColor('#c8b89a', '#2a2a4a', n);
    ctx.fillRect(0, GROUND_Y, W, H - GROUND_Y);
    // Ground line
    ctx.strokeStyle = lerpColor('#a09070', '#4a4a6a', n);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, GROUND_Y);
    ctx.lineTo(W, GROUND_Y);
    ctx.stroke();
    // Ground texture
    ctx.fillStyle = lerpColor('#b0a080', '#3a3a5a', n);
    for (let i = 0; i < 30; i++) {
      const bx = ((g1 + i * 80 + 40) % (W * 2) + W * 2) % (W * 2) - W;
      ctx.fillRect(bx, GROUND_Y + 8 + (i % 3) * 12, 20 + (i % 4) * 5, 2);
    }
  }

  function drawCloud(ctx, x, y, n) {
    const alpha = 0.8 - n * 0.5;
    ctx.fillStyle = `rgba(${Math.round(255 - 155*n)},${Math.round(255 - 155*n)},${Math.round(255 - 105*n)},${alpha})`;
    ctx.beginPath();
    ctx.ellipse(x, y, 40, 16, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(x - 20, y + 5, 24, 12, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(x + 22, y + 4, 28, 14, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  function drawDino(ctx, x, y, ducking, jumping, frame) {
    const h = ducking ? DUCK_H : DINO_H;
    ctx.save();
    // Body
    const bodyGrad = ctx.createLinearGradient(x, y, x, y + h);
    bodyGrad.addColorStop(0, '#5b8c5a');
    bodyGrad.addColorStop(1, '#3d6b3d');
    ctx.fillStyle = bodyGrad;

    if (ducking) {
      // Ducking: wider, shorter
      roundRect(ctx, x, y + 4, DINO_W + 10, h - 4, 8);
      // Head
      ctx.fillStyle = '#5b8c5a';
      roundRect(ctx, x + DINO_W + 2, y, 16, 18, 6);
    } else {
      // Body
      roundRect(ctx, x + 6, y + 12, DINO_W - 12, h - 12, 8);
      // Head
      ctx.fillStyle = '#5b8c5a';
      roundRect(ctx, x + 10, y, 30, 22, 8);
      // Tail
      ctx.fillStyle = '#4a7a4a';
      ctx.beginPath();
      ctx.moveTo(x + 6, y + 16);
      ctx.lineTo(x - 6, y + 10);
      ctx.lineTo(x + 6, y + 24);
      ctx.fill();
    }

    // Eye
    const eyeX = ducking ? x + DINO_W + 8 : x + 30;
    const eyeY = ducking ? y + 6 : y + 7;
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(eyeX, eyeY, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#222';
    ctx.beginPath();
    ctx.arc(eyeX + 1, eyeY, 2, 0, Math.PI * 2);
    ctx.fill();

    // Legs
    if (!jumping) {
      const legFrame = Math.floor(frame) % 2;
      ctx.fillStyle = '#3d6b3d';
      if (ducking) {
        ctx.fillRect(x + 10 + legFrame * 4, y + h - 4, 6, 8);
        ctx.fillRect(x + 30 - legFrame * 4, y + h - 4, 6, 8);
      } else {
        ctx.fillRect(x + 14 + legFrame * 4, y + h - 6, 6, 10);
        ctx.fillRect(x + 28 - legFrame * 4, y + h - 6, 6, 10);
      }
    } else {
      // Tucked legs while jumping
      ctx.fillStyle = '#3d6b3d';
      ctx.fillRect(x + 16, y + h - 4, 6, 6);
      ctx.fillRect(x + 26, y + h - 4, 6, 6);
    }

    // Arm
    if (!ducking) {
      ctx.fillStyle = '#4a7a4a';
      ctx.fillRect(x + 28, y + 22, 5, 10);
    }

    ctx.restore();
  }

  function drawCactus(ctx, x, y, w, h, n) {
    const grad = ctx.createLinearGradient(x, y, x + w, y + h);
    grad.addColorStop(0, lerpColor('#2d8a4e', '#2a5a2a', n));
    grad.addColorStop(1, lerpColor('#1a6a35', '#1a3a1a', n));
    ctx.fillStyle = grad;
    roundRect(ctx, x, y, w, h, 4);
    // Arms
    if (h > 40) {
      ctx.fillRect(x - 8, y + 12, 10, 6);
      ctx.fillRect(x - 8, y + 8, 6, 12);
      ctx.fillRect(x + w - 2, y + 20, 10, 6);
      ctx.fillRect(x + w + 2, y + 16, 6, 14);
    }
    // Spines
    ctx.fillStyle = lerpColor('#3aaa5e', '#3a7a3a', n);
    for (let i = 0; i < 3; i++) {
      ctx.fillRect(x + w / 2 - 1, y + 4 + i * (h / 3), 2, 4);
    }
  }

  function drawBird(ctx, x, y, frame, n) {
    const wingY = Math.sin(frame * 2) * 10;
    ctx.fillStyle = lerpColor('#8855aa', '#6a4a8a', n);
    // Body
    ctx.beginPath();
    ctx.ellipse(x + 20, y + 15, 18, 10, 0, 0, Math.PI * 2);
    ctx.fill();
    // Wing
    ctx.beginPath();
    ctx.moveTo(x + 12, y + 12);
    ctx.lineTo(x + 22, y + wingY);
    ctx.lineTo(x + 32, y + 12);
    ctx.fill();
    // Beak
    ctx.fillStyle = lerpColor('#ee9944', '#cc8844', n);
    ctx.beginPath();
    ctx.moveTo(x + 38, y + 13);
    ctx.lineTo(x + 46, y + 16);
    ctx.lineTo(x + 38, y + 18);
    ctx.fill();
    // Eye
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(x + 30, y + 12, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#222';
    ctx.beginPath();
    ctx.arc(x + 31, y + 12, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.fill();
  }

  return (
    <div className="app">
      <div className="game-wrapper">
        <div className="hud">
          <div className="score-display">
            <span className="score-label">SCORE</span>
            <span className="score-value">{String(score).padStart(5, '0')}</span>
          </div>
          <div className="score-display hi">
            <span className="score-label">HI</span>
            <span className="score-value">{String(hi).padStart(5, '0')}</span>
          </div>
        </div>
        <canvas ref={canvasRef} width={W} height={H} className="game-canvas" />
        <div className="controls-hint">
          <span>⬆ JUMP</span>
          <span>⬇ DUCK</span>
          <span>📱 TAP TO JUMP</span>
        </div>
        <div className="rotate-hint">📱 Rotate screen for best experience</div>
      </div>
    </div>
  );
}
